import { PlatformLocation } from '@angular/common';
import { Injectable } from '@angular/core';
import { HttpStatusCode } from '@office-start/common-types';
import { logRequest } from '@office-start/telemetry-service';
import { KnownRequestInstrumentationEvents } from '@office-start/telemetry-types';

import { OfficeHomePageErrorHandler } from '../../error-handler/error-handler.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownInstrumentationEvents } from '../instrumentation/instrumentation.service';
import { FetchStrategy, InstrumentedHttp, InstrumentedHttpPromise } from '../instrumentation/instrumented-http.service';
import { HttpRequestOptions } from '../models/http-request-options.model';
import { HttpResponseContentType } from '../models/http-response-content-type.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { ContextService } from '../shared-context/context.service';
import { AccessTokenConsumer, AccessTokenService } from './access-token.service';
import { AdminPolicyConfigService } from './admin-policy-config.service';
import { OfficeHomeApiConfigService } from './officehome-api-config.service';
import { SchedulerService } from './scheduler.service';
import { UserDataStorageService } from './user-data-storage.service';
import { XhrService } from './xhr.service';

export interface AdminPolicyInfo {
    PolicyValue: number;
    State: boolean;
}

export interface AdminPolicy {
    ConnectedExperience: AdminPolicyInfo;
    SendFeedback: AdminPolicyInfo;
    ScreenShot: AdminPolicyInfo;
    EmailCollection: AdminPolicyInfo;
    LogCollection: AdminPolicyInfo;
    SendSurvey: AdminPolicyInfo;
    Loop: AdminPolicyInfo;
    LoopBizChatDocs: AdminPolicyInfo;
    OptionalConnectedExperiencesEnabled: AdminPolicyInfo;
    CopilotPinningEnabled: AdminPolicyInfo;
    WebSearchEnabled: AdminPolicyInfo;
}

export interface AdminPolicyFetchResult {
    Value: AdminPolicy;
    PoliciesHash: string;
    LastUpdated: Date;
    CheckInInterval: number;
    Success: boolean;
    RequiredAdminPolicies: Set<string>;
}

@Injectable({
    providedIn: 'root'
})
export class AdminPolicyService extends XhrService {

    private fetchPolicyPromise: Promise<AdminPolicy> = null ;
    private fetchCachedPolicyPromise: Promise<AdminPolicy> = null ;

    constructor(
        private readonly globalErrorHandler: OfficeHomePageErrorHandler,
        private readonly userDataStorageService: UserDataStorageService,
        private readonly adminPolicyConfigService: AdminPolicyConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        accessTokenService: AccessTokenService,
        contextService: ContextService,
        instrumentedHttp: InstrumentedHttp,
        instrumentationService: InstrumentationService,
        officeHomeApiConfigService: OfficeHomeApiConfigService,
        scheduler: SchedulerService,
        platformLocation: PlatformLocation
    ) {
        super(
            accessTokenService,
            instrumentationService,
            instrumentedHttp,
            AccessTokenConsumer.OfficeHome,
            contextService,
            officeHomeApiConfigService,
            platformLocation,
            scheduler
        );
    }

    public async getAdminPolicy(): Promise<AdminPolicy> {
        const cachedPolicy = await this.fetchCachedPolicy();
        if (cachedPolicy) {
            // Return cached data immediately if data is valid.
            return cachedPolicy;
        }

        // Fetch policies from api, only make api call when promise is not available to prevent multiple server calls from different caller.
        if (!this.fetchPolicyPromise) {
            this.fetchPolicyPromise = new Promise<AdminPolicy>((resolve, reject) => {
                const url = `${this.contextService.sharedContext.officeHomeApiRoot}/${this.adminPolicyConfigService.config.adminPolicyApiPath}`;
                const requestConfig: HttpRequestOptions = {
                    responseType: HttpResponseContentType.Json,
                    headers: new Map()
                };

                const promiseCallback = (promise: InstrumentedHttpPromise<AdminPolicyFetchResult>) => {
                    promise
                        .instrument((result, _) => {
                            if (!result.ok) {
                                this.fetchPolicyPromise = null ;
                            }
                        })
                        .then(result => {
                            if (!result.data || !result.data.Value) {
                                // Server always return policy values (default, disabled or OCPS value), unless user is unauthenticated.
                                reject('Null admin policies response.');
                            }
                            resolve(result.data.Value);
                        })
                        .catch(error => {
                            this.fetchPolicyPromise = null ;
                            this.globalErrorHandler.handleError(error);
                        });
                };

                this.get<AdminPolicyFetchResult>(
                    KnownInstrumentationEvents.AdminPolicyServerRequest,
                    KnownRequestInstrumentationEvents.AdminPolicyServerRequest,
                    url,
                    promiseCallback,
                    reject,
                    requestConfig,
                    FetchStrategy.NetworkOnly
                );
            });
        }
        return this.fetchPolicyPromise;
    }

    private async fetchCachedPolicy(): Promise<AdminPolicy> {
        const params: InstrumentationParams = {};
        params[InstrumentationParam.Action] = 'FetchCachedAdminPolicyFromUserStorage';

        if (!this.fetchCachedPolicyPromise) {

            this.fetchCachedPolicyPromise = new Promise<AdminPolicy>(resolve => {
                // Set remoteAttempts to 1 for calls from pwa, pwa doesn't have full userStorage data at initial load
                const remoteAttempt = this.capabilitiesService.capabilities.pwaEnabled ? 1 : 0;
                let errorCode: HttpStatusCode;
                let cacheState: string;
                this.userDataStorageService.get<AdminPolicyFetchResult>(this.adminPolicyConfigService.config.cacheKey, remoteAttempt)
                    .then(cachedPolicies => {
                        if (!cachedPolicies || !cachedPolicies.Value) {
                            params[InstrumentationParam.State] = 'NotFound';
                            params[InstrumentationParam.DebugInfo] = 'No cached policies found.';
                            cacheState = 'NotFound';
                            errorCode = HttpStatusCode.CacheMiss;
                            resolve(null );
                        } else {
                            const lastUpdate = new Date(cachedPolicies.LastUpdated);
                            const expireTime = lastUpdate.getTime() + cachedPolicies.CheckInInterval * 60 * 1000;
                            const currentTime = Date.now();
                            if (expireTime < currentTime) {
                                params[InstrumentationParam.State] = 'CacheExpired';
                                params[InstrumentationParam.DebugInfo] = `Cache Expired, ExpireTime:${expireTime}`;
                                errorCode = HttpStatusCode.CacheMiss;
                                cacheState = 'CacheExpired';
                                resolve(null );
                            } else {
                                // TODO: check for missing policies in case new policies added
                                params[InstrumentationParam.State] = 'CacheFound';
                                errorCode = HttpStatusCode.OK;
                                cacheState = 'CacheFound';
                                resolve(cachedPolicies.Value);
                            }
                        }
                        this.instrumentationService.logDebugCacheEvent(KnownInstrumentationEvents.AdminPolicyCacheRequest, params);
                        // Otel logging
                        logRequest({
                            eventName: KnownRequestInstrumentationEvents.AdminPolicyCacheRequest,
                            httpCode: errorCode,
                            error: cacheState,
                            fromCache: errorCode === HttpStatusCode.OK,
                            latency: -1
                        });
                    })
                    .catch(error => {
                        this.fetchCachedPolicyPromise = null ;
                        this.globalErrorHandler.handleError(error);
                    });
            });
        }
        return this.fetchCachedPolicyPromise;
    }
}
